import Vue from 'vue'
import {make} from 'vuex-pathify'
import Constants from '@/utilities/constants'
import {assembleTravelChargeUrl, nextBusinessDay} from '@/utilities/utils'
import {ptracGet, ptracGetEntities, ptracPostEntity} from '@/utilities/ptracApi'
import { OK, CREATED } from 'http-status-codes'

const initialState = {
    poJobData: {},
    departmentOptions: null,
    createAnotherJob: false,
    ptracJob: {
        id: null,
        jobTemplateId: null,
        projectId: null,
        departmentId: null,
        processDefinitionId: null,
        processDefinitionKey: null,
        number: null,
        permitNumber: null,
        name: null,
        coordinatorId: null,
        priority: Constants.PRIORITIES.MEDIUM.VALUE,
        status: Constants.STATUSES.PENDING,
        clientId: null,
        clientContactId: null,
        builderId: null,
        builderContactId: null,
        community: {}, // Maybe map from poJobData.subdivision instead of communityId
        dateReceived: new Date(),
        targetCompletionDate: nextBusinessDay(),
        description: '',
        planNameId: null,
        elevation: null,
        createdBy: null,
        updatedBy: null,
        createdAt: null,
        updatedAt: null
    }
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
const mutations = make.mutations(state)

getters.departmentOptions = (state) => {
    return state.departmentOptions
}

getters.savePayload = (state) => {
    let data = state.ptracJob
    const payload = JSON.parse(JSON.stringify({
      endpoint: 'jobs',
      data: data
    }))
    return payload
}

mutations.setFieldsFromPO = (state) => {
    if (state.poJobData.itemDescription) {
        state.ptracJob.description = state.poJobData.itemDescription
        // Currently setting from item[0].item.itemDescription. Using task.description for Job Template. Arbitrary?
    }
    if (state.poJobData.startDate) { // Setting Target Completion Date from startDate, rather than jobStartDate
        state.ptracJob.targetCompletionDate = state.poJobData.startDate
    } else if (state.poJobData.endDate) {
        state.ptracJob.targetCompletionDate = state.poJobData.endDate
    }
    if (state.poJobData.elevation) {
        state.ptracJob.elevation = state.poJobData.elevation
    }
    if (state.poJobData.jobNum) {
        state.ptracJob.number = state.poJobData.jobNum
    }
    if (state.poJobData.permitNumber) {
        state.ptracJob.permitNumber = state.poJobData.permitNumber
    }
}

mutations.resetTemplate = (state) => {
    state.ptracJob.community = {}
    state.ptracJob.jobTemplateId = null
    state.ptracJob.clientId = null
    state.ptracJob.clientContactId = null
    state.ptracJob.builderId = null
    state.ptracJob.builderContactId = null
    state.ptracJob.processDefinitionId = null
    state.ptracJob.processDefinitionKey = null
    state.ptracJob.name = null
    state.ptracJob.priority = Constants.PRIORITIES.MEDIUM.VALUE
}

mutations.reset = () => {
    state.poJobData = {}
    state.departmentOptions = null
    state.createAnotherJob = false
    mutations.resetPtracJob()
}

mutations.resetPtracJob = () => {
    for (let f in state.ptracJob) {
        state.ptracJob[f] = null
    }
    state.ptracJob.dateReceived = new Date()
    state.ptracJob.targetCompletionDate = nextBusinessDay()
    state.ptracJob.community = {}
    state.ptracJob.priority = Constants.PRIORITIES.MEDIUM.VALUE
    state.ptracJob.status = Constants.STATUSES.PENDING
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions: {
        async populateDepartmentFromProject({ state, commit, rootState, rootGetters}) {
            state.ptracJob.departmentId = null
            state.departmentOptions = null
            const ptracProject = rootGetters['supplyPro/project/ptracProject']
            let departments = []

            for (const department of rootGetters['departments/kvpsForSelectNoIt']) {
                if (department.label === Constants.DEPARTMENTS.INSPECTIONS && ptracProject.departments.includes(department.value) ) {
                departments.push(department)
                }
                if (department.label === Constants.DEPARTMENTS.SURVEY && ptracProject.departments.includes(department.value)) {
                departments.push(department)
                }
            }
            if (departments.length === 0) {
                commit('supplyPro/project/surveyOrInspectionSelected', false, { root: true })
                return
            }
            commit('supplyPro/project/surveyOrInspectionSelected', true, { root: true })

            //Departments will always contain at least one eligible department
            state.departmentOptions = departments
            if (!departments.find(({label}) => label === Constants.DEPARTMENTS.INSPECTIONS)) {
                state.ptracJob.departmentId = departments.find(({label}) => label === Constants.DEPARTMENTS.SURVEY).value
                return
            }
            state.ptracJob.departmentId = departments.find(({label}) => label === Constants.DEPARTMENTS.INSPECTIONS).value // Defaulting to Inspections. Is this redundant with departmentOptions watcher in Job?
        },
        async setPoJobData({ state, rootState }) { //Probably refactor. Renaming keys and duplicate keys makes normal looping difficult
            const poPath = rootState.supplyPro.purchaseOrder.purchaseOrder
            const headerKeys = [
                Constants.SUPPLYPRO.JOB_FIELDS.START_DATE,
                Constants.SUPPLYPRO.JOB_FIELDS.END_DATE,
                Constants.SUPPLYPRO.JOB_FIELDS.BUYER_STATUS,
                Constants.SUPPLYPRO.JOB_FIELDS.ORDER_HEADER_NOTE,
                Constants.SUPPLYPRO.JOB_FIELDS.ISSUE_DATE,
                Constants.SUPPLYPRO.JOB_FIELDS.ORDER_TYPE]
            const jobKeys = [
                Constants.SUPPLYPRO.JOB_FIELDS.JOB_NUM,
                Constants.SUPPLYPRO.JOB_FIELDS.PERMIT_NUMBER,
                Constants.SUPPLYPRO.JOB_FIELDS.ELEVATION,
                Constants.SUPPLYPRO.JOB_FIELDS.PRIMARY_FIRST_NAME,
                Constants.SUPPLYPRO.JOB_FIELDS.PRIMARY_LAST_NAME,
                Constants.SUPPLYPRO.JOB_FIELDS.PRIMARY_PHONE_NUMBER,
                Constants.SUPPLYPRO.JOB_FIELDS.PRIMARY_EMAIL_ADDRESS,
                Constants.SUPPLYPRO.JOB_FIELDS.COMMUNITY_ID,
                Constants.SUPPLYPRO.JOB_FIELDS.SUBDIVISION]

            for (const key in poPath.header) {
                if (headerKeys.includes(key)) {
                    Object.assign(state.poJobData, {[key]: poPath.header[key]})
                }
            }
            if (poPath.header.builder.name != null) {
                state.poJobData.builderName = poPath.header.builder.name
            }
            for (const key in poPath.header.task) {
                if (key === 'name') {
                    state.poJobData.taskName = poPath.header.task[key]
                } else if (poPath.header.task[key] != null) {
                    Object.assign(state.poJobData, {[key]: poPath.header.task[key]})
                }
            }
            for (const key in poPath.header.job) {
                if (key === Constants.SUPPLYPRO.JOB_FIELDS.START_DATE) {
                    state.poJobData.jobStartDate = poPath.header.job[key]
                }
                if (key === Constants.SUPPLYPRO.JOB_FIELDS.END_DATE) {
                    state.poJobData.jobEndDate = poPath.header.job[key]
                }
                if (jobKeys.includes(key)) {
                    Object.assign(state.poJobData, {[key]: poPath.header.job[key]})
                }
            }
            if (poPath.issueDate != null ) {
                state.ptracJob.dateReceived = poPath.issueDate
            }
            for (const key in poPath.items[0]) {
                if (poPath.items[0][key] != null) {
                    Object.assign(state.poJobData, {[key]: poPath.items[0][key]})
                }
            }
        },
        async fetchJobNumberExists({state}) {
            return await ptracGetEntities('jobs/search/existsByNumberAndIdNot?number=' + state.ptracJob.number + '&id=' + state.ptracJob.id)
        },
        async fetchTravelCharge({state}) {
            return await ptracGet(assembleTravelChargeUrl(state.ptracJob))
        },
        async createJob({state, rootState, dispatch, commit}) {
            let response = await ptracPostEntity('jobs', state.ptracJob)
            if (OK !== response.status && CREATED !== response.status) {
                return response
            }
            const jobId = response.data.id

            // Create metadata to attach PO id to job
            return await ptracPostEntity('supply-pro-purchase-orders/attachPurchaseOrderIdToJob', {'attachToEntityId': jobId, 'value': rootState.supplyPro.purchaseOrder.purchaseOrder.id})
        },
    },
}
